import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutGrid, CreditCard, Users, BarChart3, MessageSquare, CalendarClock, Store, Wallet, LogOut, UserCog, Clock3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/xuma/Logo";

const baseNav = [
  { to: "/dashboard", label: "Panel", icon: LayoutGrid },
  { to: "/editor", label: "Mi tarjeta", icon: CreditCard },
  { to: "/crm", label: "CRM", icon: Users },
  { to: "/conversations", label: "Chats", icon: MessageSquare },
  { to: "/appointments", label: "Citas", icon: CalendarClock },
  { to: "/stats", label: "Estadísticas", icon: BarChart3 },
];

export default function DashLayout({ children, title }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const nav = [...baseNav];
  if (user?.role === "admin") nav.push({ to: "/admin/distributors", label: "Distribuidores", icon: Store });
  if (user?.role === "admin") nav.push({ to: "/admin/clients", label: "Clientes", icon: UserCog });
  if (user?.role === "admin") nav.push({ to: "/admin/waitlist", label: "Lista de espera", icon: Clock3 });
  if (user?.role === "distributor") nav.push({ to: "/distributor", label: "Comisiones", icon: Wallet });

  const handleLogout = async () => {
    await logout();
    toast.success("Sesión cerrada");
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-xuma-dash lg:flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex flex-col w-60 shrink-0 bg-white border-r border-xuma-border sticky top-0 h-screen">
        <div className="h-16 flex items-center px-6 border-b border-xuma-border">
          <Logo variant="dark" size="sm" />
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => {
            const active = pathname === n.to;
            return (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={`flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                  active ? "bg-xuma-primary text-white" : "text-xuma-muted hover:bg-xuma-dash hover:text-xuma-dark"
                }`}>
                <n.icon className="h-4 w-4" /> {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-xuma-border">
          <div className="flex items-center gap-2 px-2 py-2">
            {user?.picture
              ? <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
              : <span className="h-8 w-8 rounded-full bg-xuma-primary/10 grid place-items-center text-xuma-primary text-sm font-bold">{user?.name?.[0] || "U"}</span>}
            <span className="text-sm text-xuma-dark truncate flex-1">{user?.name}</span>
          </div>
          <button onClick={handleLogout} data-testid="logout-btn" className="w-full flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm text-xuma-muted hover:bg-xuma-dash">
            <LogOut className="h-4 w-4" /> Salir
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden">
        <header className="bg-white border-b border-xuma-border sticky top-0 z-40">
          <div className="h-14 px-4 flex items-center justify-between">
            <Logo variant="dark" size="sm" showTagline={false} />
            <button onClick={handleLogout} className="text-xuma-muted"><LogOut className="h-5 w-5" /></button>
          </div>
          <nav className="flex overflow-x-auto border-t border-xuma-border">
            {nav.map((n) => {
              const active = pathname === n.to;
              return (
                <Link key={n.to} to={n.to}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm whitespace-nowrap ${active ? "text-xuma-primary border-b-2 border-xuma-primary" : "text-xuma-muted"}`}>
                  <n.icon className="h-4 w-4" /> {n.label}
                </Link>
              );
            })}
          </nav>
        </header>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="max-w-5xl mx-auto px-5 py-8 lg:py-10">
          {title && <h1 className="font-heading font-extrabold text-3xl text-xuma-dark tracking-tight mb-6">{title}</h1>}
          {children}
        </div>
      </main>
    </div>
  );
}
